import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogChildFever = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="/images/21.png"
          alt="Child Fever"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-purple-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">PEDIATRICS</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              Child Fever: When to Wait and When to See a Doctor Immediately
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />5 Apr 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />5 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">Child Fever: When to Wait and When to See a Doctor</span>
        </div>
      </div>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Article */}
            <div className="lg:col-span-2 text-gray-700 leading-relaxed">

              <p className="text-lg leading-relaxed mb-8 border-l-4 border-purple-500 pl-5 bg-purple-50 py-4 pr-4 rounded-r-lg">
                Fever is one of the most common reasons parents bring their child to a doctor — and also one of the most frequently mismanaged at home. Most fevers in children are caused by viral infections that resolve without any treatment beyond rest, fluids, and comfort measures. But some fevers signal serious bacterial infections or other conditions that require urgent medical attention. Knowing the difference can prevent unnecessary emergency visits while ensuring you act quickly when it truly matters.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-purple-500/25">What Is a Fever and Why Does It Happen?</h2>
              <p className="mb-4">A fever is defined as a body temperature above 38°C (100.4°F) measured rectally in infants, or above 37.5°C measured orally in older children. Fever is not a disease — it is a symptom and a physiological response. When the body detects an infection, the immune system deliberately raises body temperature to create an environment less hospitable to bacteria and viruses, while simultaneously accelerating immune cell activity.</p>
             

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-purple-500/25">Fever by Age: Different Rules Apply</h2>
              <div className="space-y-3 mb-10">
                {[
                  { t: 'Under 3 months — Always seek immediate medical attention', d: 'Any fever above 38°C in a baby under 3 months old requires immediate medical assessment, even if the baby appears well. Very young infants have immature immune systems and limited ability to localise infection, meaning what appears to be a simple viral fever can mask serious bacterial infection including meningitis, sepsis, or urinary tract infection. Do not wait to see if it improves — go to a doctor or emergency department immediately.' },
                  { t: '3 to 6 months — Seek medical review the same day', d: 'Fever in this age group warrants same-day medical assessment. Your doctor will examine the baby, check for a source of infection, and decide whether investigation or treatment is needed. While serious bacterial infection is less common than in the under-3-month group, the risk remains elevated enough to warrant professional assessment rather than home management alone.' },
                  { t: '6 months to 2 years — Monitor carefully, know the warning signs', d: 'Most fevers in this age group are caused by common viral infections — colds, ear infections, roseola, and similar illnesses. Home management with fluids, rest, and paracetamol or ibuprofen for comfort is appropriate in most cases. However, you should contact a doctor if the fever exceeds 39°C, lasts more than 3 days, or if your child appears unusually unwell, refuses fluids, or develops any of the warning signs listed below.' },
                  { t: 'Over 2 years — Most fevers can be managed at home', d: 'Older children with fever and no other concerning symptoms can typically be managed at home. Focus on keeping them hydrated, comfortable, and rested. Fever-reducing medication (paracetamol or ibuprofen at appropriate doses) can be used to improve comfort if the child is distressed — but is not required simply because the temperature is high. Follow up with a doctor if fever persists beyond 3 to 5 days or warning signs develop.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

              {/* Mid article image */}
              <div className="my-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <img
                    src="/images/22.png"
                    alt="Child being examined by doctor"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic text-center">Knowing when to seek medical assessment and when to manage at home is the most important skill for parents.</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">How to Measure Fever Accurately</h3>
                  <p className="text-sm leading-relaxed mb-3">The method you use to measure temperature significantly affects accuracy. Rectal temperature is the most accurate in infants under 2 years. Oral temperature (under the tongue) is reliable in children over 4 years who can cooperate. Axillary (armpit) measurement is convenient but least accurate — add 0.5°C to the reading for a rough estimate of true temperature.</p>
                  <p className="text-sm leading-relaxed mb-3">Forehead strip thermometers and many ear thermometers are unreliable and not recommended for clinical decision-making. A digital thermometer is inexpensive and accurate — it is worth having one at home.</p>
                
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-purple-500/25">Warning Signs — Seek Medical Attention Immediately</h2>
              <p className="mb-5">Regardless of your child's age or the height of the fever, seek urgent medical assessment if any of the following are present:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
                {[
                  'Difficulty breathing or rapid, laboured breathing',
                  'A rash that does not fade when pressed (glass test)',
                  'Extreme lethargy — cannot be roused or unusually unresponsive',
                  'Severe headache combined with neck stiffness',
                  'Sensitivity to light (photophobia)',
                  'Persistent vomiting or inability to keep fluids down',
                  'Signs of dehydration: no wet nappy for 8+ hours, no tears, dry mouth',
                  'Febrile convulsion (seizure) — call emergency services',
                  'Bulging fontanelle (soft spot on head) in infants',
                  'Fever returning after 24 hours of being normal',
                  'Child looks or behaves very differently from normal',
                  'Fever above 40°C at any age',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                    <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                    <p className="text-sm text-red-800">{s}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-purple-500/25">Home Management of Fever</h2>
              <div className="space-y-4 mb-10">
                {[
                  { n: '01', t: 'Keep Your Child Hydrated', d: 'Fever increases fluid loss through sweating and faster breathing. Offer fluids frequently — water, diluted juice, oral rehydration solution, or breast milk in infants. A child who is refusing all fluids and showing signs of dehydration needs medical assessment. Popsicles and ice chips can help older children who resist drinking.' },
                  { n: '02', t: 'Use Fever-Reducing Medication Appropriately', d: 'Paracetamol (acetaminophen) and ibuprofen are safe and effective for reducing fever and improving comfort in children. Use age and weight-appropriate doses — always check the packaging or ask your pharmacist. Do not use aspirin in children under 16. Ibuprofen should not be given to children under 3 months or to children who are dehydrated. Alternating paracetamol and ibuprofen is sometimes recommended by doctors for high fevers — ask your paediatrician.' },
                  { n: '03', t: 'Keep Your Child Comfortable', d: 'Dress your child in light clothing and keep the room at a comfortable temperature. Overdressing traps heat and worsens fever. Lukewarm sponging can provide brief comfort but does not sustainably lower temperature. Cold baths or ice packs should never be used — they cause shivering which actually raises core temperature.' },
                  { n: '04', t: 'Rest and Monitor', d: 'Most viral fevers resolve within 3 to 5 days. Rest is important but your child does not need to be confined to bed if they feel well enough to be active. Monitor symptoms closely, check temperature every few hours, and keep a record of readings. Contact a doctor if the fever is not improving after 3 days or if your child\'s condition deteriorates.' },
                ].map(item => (
                  <div key={item.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-5">
                    <div className="bg-purple-500 text-white font-bold text-sm w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">{item.n}</div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.t}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-purple-50 border border-purple-100 rounded-2xl p-7 mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-3">When to Follow Up with Your Paediatrician</h2>
                <ul className="space-y-2">
                  {[
                    'Fever lasting more than 3 days in a child over 2 years',
                    'Fever returning after a fever-free period of 24 hours',
                    'Your child is not returning to their normal self after the fever resolves',
                    'You are worried — trust your instinct as a parent',
                    'This is the third or more fever episode in a short period',
                    'Your child has an underlying health condition that makes fever higher risk',
                  ].map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                  <ChevronLeft size={16} /> Back to All Blogs
                </Link>
               
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-7">
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'Fever defined as above', v: '38°C rectal' },
                    { l: 'Under 3 months with fever', v: 'See doctor immediately' },
                    { l: 'Most fevers resolve within', v: '3–5 days' },
                    { l: 'Most common cause', v: 'Viral infection' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <h3 className="font-bold text-red-800 text-sm mb-3">Seek Emergency Care If:</h3>
                {['Rash that does not fade when pressed', 'Neck stiffness with fever', 'Difficulty breathing', 'Cannot be roused', 'Under 3 months with any fever'].map((s, i) => (
                  <div key={i} className="flex items-start gap-2 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                    <p className="text-xs text-red-700">{s}</p>
                  </div>
                ))}
              </div>

              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Find a Paediatrician</h3>
                <p className="text-white/80 text-xs mb-4">Book a paediatric consultation on Docmize today.</p>
                <Link to="/doctors?specialty=Pediatrician" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Find a Paediatrician
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogChildFever;